/**
 * MetaFlexibleAdDetails — Ad details form for Meta Flexible Ad format.
 *
 * Each creative is a self-contained group (image/video + primary text + headline +
 * description). Unlike Dynamic Creative, assets and copy stay together — Meta tests
 * which complete creative performs best rather than mixing all combinations freely.
 *
 * - Up to 10 creatives, each with its own asset and copy fields
 * - Destination URL and CTA are shared across all creatives
 * - Click any creative card to preview it in the mockup
 * - No ratio tabs — Meta auto-crops assets to fit each placement
 * - Delete button appears on card hover (top-right corner)
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Trash2, Upload, X } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { createMediaBlobUrl } from '@/utils/blobMediaType';
import { seedSignature } from './seedSignature';
import type { FieldIssue } from '../../types';
import type { Campaign, ValidationFeedback } from '@/api/campaigns';
import { GearExchangeModal, type GXImportedFile } from '../gear-exchange/GearExchangeModal';
import { GearExchangeImportButton } from '../gear-exchange/GearExchangeImportButton';
import { FieldLabel, inputClass, InlineIssue, Field, issueBorder } from './shared';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_CARDS = 10;
const PRIMARY_TEXT_LIMIT = 125;
const HEADLINE_LIMIT = 40;
const DESCRIPTION_LIMIT = 25;

const CTA_OPTIONS = [
  'Apply Now',
  'Book Now',
  'Contact Us',
  'Get Offer',
  'Get Quote',
  'Learn More',
  'Order Now',
  'Shop Now',
  'Sign Up',
];

// ─── Exported types ───────────────────────────────────────────────────────────

export interface FlexibleInitialCard {
  existingAssetId: string;
  imageUrl: string;
  contentType?: string | null;
  primaryText: string;
  headline: string;
  description: string;
}

export interface MetaFlexibleInitialValues {
  cards?: FlexibleInitialCard[];
  cta?: string;
  url?: string;
  legal?: string;
}

export interface FlexibleCardState {
  existingAssetId: string | null;
  imageUrl: string | null;
  file: File | null;
  primaryText: string;
  headline: string;
  description: string;
}

// ─── Internal types ───────────────────────────────────────────────────────────

interface CreativeCard {
  id: number;
  existingAssetId: string | null;
  imageUrl: string | null;
  contentType?: string | null;
  file: File | null;
  primaryText: string;
  headline: string;
  description: string;
}

let nextId = 1;

function makeCard(): CreativeCard {
  return {
    id: nextId++,
    existingAssetId: null,
    imageUrl: null,
    contentType: null,
    file: null,
    primaryText: '',
    headline: '',
    description: '',
  };
}

function makeCardFromInitial(init: FlexibleInitialCard): CreativeCard {
  return {
    id: nextId++,
    existingAssetId: init.existingAssetId,
    imageUrl: init.imageUrl,
    contentType: init.contentType ?? null,
    file: null,
    primaryText: init.primaryText,
    headline: init.headline,
    description: init.description,
  };
}

// ─── Video first-frame extractors ─────────────────────────────────────────────

function extractVideoFirstFrame(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.src = url;
    video.currentTime = 0.1;
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    });
    video.addEventListener('error', () => {
      URL.revokeObjectURL(url);
      reject(new Error('Video load failed'));
    });
    video.load();
  });
}

function extractVideoFirstFrameFromUrl(url: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.crossOrigin = 'anonymous';
    video.src = url;
    video.currentTime = 0.1;
    video.addEventListener('seeked', () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext('2d')!.drawImage(video, 0, 0);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    });
    video.addEventListener('error', () => reject(new Error('Video load failed')));
    video.load();
  });
}

// ─── Card row ─────────────────────────────────────────────────────────────────

function InlineIssues({
  issue,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
}: {
  issue: FieldIssue | undefined;
  showErrors: boolean;
  showWarnings: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
}) {
  return (
    <InlineIssue
      issue={issue}
      showErrors={showErrors}
      showWarnings={showWarnings}
      feedback={feedback}
      feedbackPending={feedbackPending}
      onFeedback={onFeedback}
    />
  );
}

function CardRow({
  card,
  index,
  total,
  isActive,
  fieldIssues,
  showErrors,
  showWarnings,
  feedback,
  feedbackPending,
  onFeedback,
  onSelect,
  onFileChange,
  onExistingImageClear,
  onPrimaryTextChange,
  onHeadlineChange,
  onDescriptionChange,
  onRemove,
}: {
  card: CreativeCard;
  index: number;
  total: number;
  isActive: boolean;
  fieldIssues: Record<string, FieldIssue>;
  showErrors: boolean;
  showWarnings: boolean;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onSelect: () => void;
  onFileChange: (f: File | null) => void;
  onExistingImageClear: () => void;
  onPrimaryTextChange: (v: string) => void;
  onHeadlineChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onRemove: () => void;
}) {
  const primaryIssue = fieldIssues[`primary_text.${index}`];
  const headlineIssue = fieldIssues[`headline.${index}`];
  const descriptionIssue = fieldIssues[`description.${index}`];
  const inputRef = useRef<HTMLInputElement>(null);
  const [thumbUrl, setThumbUrl] = useState<string | undefined>();

  useEffect(() => {
    if (card.file) {
      if (card.file.type.startsWith('video/')) {
        extractVideoFirstFrame(card.file)
          .then(setThumbUrl)
          .catch(() => setThumbUrl(undefined));
      } else {
        setThumbUrl(createMediaBlobUrl(card.file));
      }
    } else if (card.imageUrl) {
      const isVideo = card.contentType?.startsWith('video/');
      if (isVideo) {
        extractVideoFirstFrameFromUrl(card.imageUrl)
          .then(setThumbUrl)
          .catch(() => setThumbUrl(card.imageUrl ?? undefined));
      } else {
        setThumbUrl(card.imageUrl);
      }
    } else {
      setThumbUrl(undefined);
    }
  }, [card.file, card.imageUrl, card.contentType]);

  const hasThumb = !!thumbUrl;
  const hasExistingImage = !!card.existingAssetId && !card.file;

  return (
    <div
      className={`relative group/card flex items-center gap-3 p-3 rounded-xl border transition-all cursor-pointer ${
        isActive ? 'border-slate-200 bg-slate-50' : 'border-slate-200 hover:border-slate-300'
      }`}
      onClick={onSelect}
    >
      {/* ── Delete card — top-right, hover only ── */}
      <div
        className="absolute top-2 right-2 opacity-0 group-hover/card:opacity-100 transition-opacity"
        onClick={(e) => e.stopPropagation()}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <span>
              <button
                type="button"
                onClick={onRemove}
                disabled={total <= 1}
                className="w-6 h-6 rounded flex items-center justify-center text-red-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-25 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            {total <= 1 ? 'Cannot delete — at least one creative required' : 'Remove creative'}
          </TooltipContent>
        </Tooltip>
      </div>

      {/* ── Thumbnail ── */}
      <div className="w-[88px] flex-shrink-0">
        <div
          onClick={(e) => {
            e.stopPropagation();
            inputRef.current?.click();
          }}
          className="relative w-full aspect-square rounded-lg overflow-hidden bg-slate-100 border border-slate-200 group/thumb hover:border-slate-300 transition-colors cursor-pointer"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/*,video/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onFileChange(f);
            }}
          />
          {hasThumb ? (
            <>
              <img
                src={thumbUrl}
                alt={`Creative ${index + 1}`}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
                <Upload className="w-4 h-4 text-white" />
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (hasExistingImage) {
                    onExistingImageClear();
                  } else {
                    onFileChange(null);
                  }
                }}
                className="absolute top-1 right-1 w-4 h-4 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover/thumb:opacity-100 transition-opacity z-10"
              >
                <X className="w-2.5 h-2.5" />
              </button>
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1">
              <Upload className="w-4 h-4 text-slate-400" />
              <span className="text-[9px] text-slate-400 font-medium">Image / Video</span>
            </div>
          )}
          <div className="absolute top-1 left-1 w-4 h-4 rounded-full text-[9px] font-bold flex items-center justify-center bg-black/25 text-white">
            {index + 1}
          </div>
        </div>
      </div>

      {/* ── Copy fields ── */}
      <div className="flex-1 min-w-0 flex flex-col gap-2" onClick={(e) => e.stopPropagation()}>
        <span
          className={`text-[10px] font-bold tracking-wider ${isActive ? 'text-slate-600' : 'text-slate-400'}`}
        >
          CREATIVE {index + 1}
        </span>

        {/* Primary text */}
        <div>
          <div className="relative">
            <textarea
              value={card.primaryText}
              onChange={(e) => onPrimaryTextChange(e.target.value)}
              placeholder="Primary text..."
              rows={2}
              className={`${inputClass} text-xs resize-none pr-10 ${showErrors && (primaryIssue?.errors?.length ?? 0) > 0 ? 'border-red-400 focus:border-red-400' : showWarnings && (primaryIssue?.warnings?.length ?? 0) > 0 ? 'border-amber-400 focus:border-amber-400' : ''}`}
            />
            <span
              className={`absolute right-3 bottom-2 text-[10px] tabular-nums pointer-events-none ${
                card.primaryText.length > PRIMARY_TEXT_LIMIT
                  ? 'text-amber-600 font-medium'
                  : 'text-slate-400'
              }`}
            >
              {card.primaryText.length}/{PRIMARY_TEXT_LIMIT}
            </span>
          </div>
          <InlineIssues
            issue={primaryIssue}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onFeedback}
          />
        </div>

        {/* Headline */}
        <div>
          <div className="relative">
            <input
              type="text"
              value={card.headline}
              onChange={(e) => onHeadlineChange(e.target.value)}
              placeholder="Headline..."
              className={`${inputClass} text-xs pr-14 ${showErrors && (headlineIssue?.errors?.length ?? 0) > 0 ? 'border-red-400 focus:border-red-400' : showWarnings && (headlineIssue?.warnings?.length ?? 0) > 0 ? 'border-amber-400 focus:border-amber-400' : ''}`}
            />
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums pointer-events-none ${
                card.headline.length > HEADLINE_LIMIT
                  ? 'text-amber-600 font-medium'
                  : 'text-slate-400'
              }`}
            >
              {card.headline.length}/{HEADLINE_LIMIT}
            </span>
          </div>
          <InlineIssues
            issue={headlineIssue}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onFeedback}
          />
        </div>

        {/* Description */}
        <div>
          <div className="relative">
            <input
              type="text"
              value={card.description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Description..."
              className={`${inputClass} text-xs pr-10 ${showErrors && (descriptionIssue?.errors?.length ?? 0) > 0 ? 'border-red-400 focus:border-red-400' : showWarnings && (descriptionIssue?.warnings?.length ?? 0) > 0 ? 'border-amber-400 focus:border-amber-400' : ''}`}
            />
            <span
              className={`absolute right-3 top-1/2 -translate-y-1/2 text-[10px] tabular-nums pointer-events-none ${
                card.description.length > DESCRIPTION_LIMIT
                  ? 'text-amber-600 font-medium'
                  : 'text-slate-400'
              }`}
            >
              {card.description.length}/{DESCRIPTION_LIMIT}
            </span>
          </div>
          <InlineIssues
            issue={descriptionIssue}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onFeedback}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export interface MetaFlexibleAdDetailsProps {
  campaignId?: string;
  fordshareIds?: string[] | null;
  campaign?: Campaign | null;
  initialValues?: MetaFlexibleInitialValues;
  showErrors: boolean;
  showWarnings: boolean;
  fieldIssues: Record<string, FieldIssue>;
  feedback?: Record<string, ValidationFeedback>;
  feedbackPending?: Set<string>;
  onAIFeedback?: (
    warningKey: string,
    warningMessage: string,
    action: 'accepted' | 'dismissed',
  ) => void;
  onImageUrlChange: (url: string | undefined) => void;
  onCaptionChange: (v: string) => void;
  onHeadlineChange: (v: string) => void;
  onDescriptionChange: (v: string) => void;
  onCtaChange: (v: string) => void;
  onUrlChange?: (v: string) => void;
  onLegalChange?: (v: string) => void;
  onCardsChange?: (cards: FlexibleCardState[], deletedAssetIds: string[]) => void;
  usedInVariants?: Map<string, number[]>;
  gxData?: import('@/api/gearExchange').GXMultiSearchResponse | null;
  gxLoading?: boolean;
  gxError?: boolean;
}

export function MetaFlexibleAdDetails({
  campaignId,
  fordshareIds,
  campaign,
  initialValues,
  showErrors,
  showWarnings,
  fieldIssues,
  feedback,
  feedbackPending,
  onAIFeedback,
  onImageUrlChange,
  onCaptionChange,
  onHeadlineChange,
  onDescriptionChange,
  onCtaChange,
  onUrlChange,
  onLegalChange,
  onCardsChange,
  usedInVariants,
  gxData,
  gxLoading,
  gxError,
}: MetaFlexibleAdDetailsProps) {
  const [cards, setCards] = useState<CreativeCard[]>(() =>
    initialValues?.cards?.length ? initialValues.cards.map(makeCardFromInitial) : [makeCard()],
  );
  const [activeCard, setActiveCard] = useState(0);
  const [deletedAssetIds, setDeletedAssetIds] = useState<string[]>([]);
  // Signature of the cards last seeded from — null means "never seeded".
  const seededSigRef = useRef<string | null>(null);
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const bulkInputRef = useRef<HTMLInputElement>(null);
  const [destinationUrl, setDestinationUrl] = useState(initialValues?.url ?? '');
  const [cta, setCta] = useState(initialValues?.cta ?? 'Learn More');
  const [legal, setLegal] = useState(initialValues?.legal ?? '');

  // Gear Exchange state
  const [showGxModal, setShowGxModal] = useState(false);

  // Count cards with actual images (not empty placeholders)
  const cardsWithImages = cards.filter((c) => c.file || c.imageUrl).length;

  // Notify parent of all card state + deleted IDs
  const notifyCardsChange = useCallback(
    (nextCards: CreativeCard[], nextDeletedIds: string[]) => {
      onCardsChange?.(
        nextCards.map((c) => ({
          existingAssetId: c.existingAssetId,
          imageUrl: c.imageUrl,
          file: c.file,
          primaryText: c.primaryText,
          headline: c.headline,
          description: c.description,
        })),
        nextDeletedIds,
      );
    },
    [onCardsChange],
  );

  // Notify parent with the active card's values for mockup preview
  const notifyMockup = useCallback(
    (updatedCards: CreativeCard[], activeIndex: number) => {
      const card = updatedCards[activeIndex];
      if (!card) return;
      const imageUrl = card.file ? createMediaBlobUrl(card.file) : (card.imageUrl ?? undefined);
      onImageUrlChange(imageUrl);
      onCaptionChange(card.primaryText);
      onHeadlineChange(card.headline);
      onDescriptionChange(card.description);
    },
    [onImageUrlChange, onCaptionChange, onHeadlineChange, onDescriptionChange],
  );

  // On mount: sync all initial state up to parent
  useEffect(() => {
    notifyMockup(cards, activeCard);
    notifyCardsChange(cards, deletedAssetIds);
    onCtaChange(cta);
    onUrlChange?.(destinationUrl);
    onLegalChange?.(legal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-seed from the DB whenever the incoming asset identities change.
  // Same guard as the carousel/story/dynamic-creative components — see
  // seedSignature.ts. The useState initialiser above only runs on mount, which
  // left this component dependent on the parent's formKey remount to pick up new
  // data. An empty signature is never seeded from, because the parent publishes
  // an empty list as a loading placeholder while URL resolution is in flight.
  useEffect(() => {
    if (!initialValues?.cards) return;
    const sig = seedSignature(initialValues.cards);
    if (!sig || sig === seededSigRef.current) return;
    seededSigRef.current = sig;
    const next = initialValues.cards.map(makeCardFromInitial);
    setCards(next);
    setActiveCard(0);
    // A re-seed adopts server state wholesale, so deletions staged against the
    // previous seed are already reflected in initialValues.cards.
    setDeletedAssetIds([]);
    notifyMockup(next, 0);
    notifyCardsChange(next, []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues?.cards]);

  function selectCard(index: number) {
    setActiveCard(index);
    notifyMockup(cards, index);
  }

  function updateCard(id: number, patch: Partial<Omit<CreativeCard, 'id'>>) {
    setCards((prev) => {
      const next = prev.map((c) => (c.id === id ? { ...c, ...patch } : c));
      const updatedIndex = next.findIndex((c) => c.id === id);
      if (updatedIndex === activeCard) notifyMockup(next, activeCard);
      notifyCardsChange(next, deletedAssetIds);
      return next;
    });
  }

  function handleFileChange(id: number, f: File | null) {
    setCards((prev) => {
      const card = prev.find((c) => c.id === id);
      // When replacing an existing asset with a new file, queue old asset for deletion
      let nextDeleted = deletedAssetIds;
      if (f && card?.existingAssetId) {
        nextDeleted = [...deletedAssetIds, card.existingAssetId];
        setDeletedAssetIds(nextDeleted);
      }
      const next = prev.map((c) =>
        c.id === id
          ? {
              ...c,
              file: f,
              existingAssetId: f ? null : c.existingAssetId,
              imageUrl: f ? null : c.imageUrl,
            }
          : c,
      );
      const updatedIndex = next.findIndex((c) => c.id === id);
      if (updatedIndex === activeCard) notifyMockup(next, activeCard);
      notifyCardsChange(next, nextDeleted);
      return next;
    });
  }

  function handleExistingImageClear(id: number) {
    setCards((prev) => {
      const card = prev.find((c) => c.id === id);
      const nextDeleted = card?.existingAssetId
        ? [...deletedAssetIds, card.existingAssetId]
        : deletedAssetIds;
      if (card?.existingAssetId) setDeletedAssetIds(nextDeleted);
      const next = prev.map((c) =>
        c.id === id ? { ...c, existingAssetId: null, imageUrl: null, file: null } : c,
      );
      if (next.findIndex((c) => c.id === id) === activeCard) notifyMockup(next, activeCard);
      notifyCardsChange(next, nextDeleted);
      return next;
    });
  }

  function removeCard(id: number) {
    setCards((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (!next.length) return prev;
      const removedCard = prev.find((c) => c.id === id);
      const removedIndex = prev.findIndex((c) => c.id === id);
      const newActive = removedIndex <= activeCard ? Math.max(0, activeCard - 1) : activeCard;
      setActiveCard(newActive);
      // Queue existing asset for deletion
      const nextDeleted = removedCard?.existingAssetId
        ? [...deletedAssetIds, removedCard.existingAssetId]
        : deletedAssetIds;
      if (removedCard?.existingAssetId) setDeletedAssetIds(nextDeleted);
      notifyMockup(next, newActive);
      notifyCardsChange(next, nextDeleted);
      return next;
    });
  }

  function handleUrlChange(v: string) {
    setDestinationUrl(v);
    onUrlChange?.(v);
  }

  function handleCtaChange(v: string) {
    setCta(v);
    onCtaChange(v);
  }

  function handleLegalChange(v: string) {
    setLegal(v);
    onLegalChange?.(v);
  }

  function addBulkFiles(fileList: FileList | File[]) {
    const files = Array.from(fileList);
    if (files.length === 0) return;
    setCards((prev) => {
      const next = [...prev];
      let fileIdx = 0;
      // Fill empty cards first (cards with no file and no existing image)
      for (let i = 0; i < next.length && fileIdx < files.length; i++) {
        if (!next[i].file && !next[i].imageUrl) {
          next[i] = { ...next[i], file: files[fileIdx++] };
        }
      }
      // Create new cards for remaining files
      while (fileIdx < files.length && next.length < MAX_CARDS) {
        const newCard = makeCard();
        newCard.file = files[fileIdx++];
        next.push(newCard);
      }
      notifyCardsChange(next, deletedAssetIds);
      notifyMockup(next, activeCard);
      return next;
    });
  }

  function onZoneDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(true);
  }
  function onZoneDragLeave(e: React.DragEvent) {
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDraggingFile(false);
  }
  function onZoneDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDraggingFile(false);
    addBulkFiles(e.dataTransfer.files);
  }

  // Validation helpers
  const assetIssue = fieldIssues['asset_files'] ?? fieldIssues['Ratio_items'];
  const urlIssue = fieldIssues['destination_url'];
  const primaryTextIssue = fieldIssues['primary_text'];
  const headlineIssue = fieldIssues['headline'];
  const descriptionIssue = fieldIssues['description'];

  const addButtonHasError = showErrors && (assetIssue?.errors?.length ?? 0) > 0;
  const addButtonHasWarning = showWarnings && (assetIssue?.warnings?.length ?? 0) > 0;
  const addButtonBorder = addButtonHasError
    ? 'border-red-400 hover:border-red-500 text-red-500 hover:text-red-600'
    : addButtonHasWarning
      ? 'border-amber-400 hover:border-amber-500 text-amber-500 hover:text-amber-600'
      : 'border-slate-200 hover:border-slate-300 text-slate-400 hover:text-slate-500';

  return (
    <div className="flex-1 overflow-y-auto hover-scroll px-6 py-5 space-y-6 min-h-0">
      {/* ── About ───────────────────────────────────────────────────── */}
      <div className="border-l-2 border-[#0014cc]/30 pl-3">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">
          About this format
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          A Flexible Ad bundles multiple self-contained creatives into a single ad entry, letting
          Meta determine which performs best for each user. Each creative is a{' '}
          <span className="font-medium text-slate-700">complete group</span> — one image or video
          paired with its own primary text, headline, and description. Meta automatically crops
          assets to fit each placement. Add up to{' '}
          <span className="font-medium text-slate-700">10 creatives</span> and Meta tests and serves
          whichever performs best.{' '}
          <a
            href="https://www.facebook.com/business/ads-guide"
            target="_blank"
            rel="noopener noreferrer"
            className="text-slate-400 hover:text-slate-600 underline underline-offset-2 decoration-slate-300"
          >
            Meta Flexible Ad specs ↗
          </a>
        </p>
      </div>

      {/* ── Gear Exchange Import ──────────────────────────────────── */}
      {
        <>
          <GearExchangeImportButton
            totalAssets={gxData?.total_assets ?? null}
            loading={gxLoading}
            error={gxError}
            fordshareIds={fordshareIds ?? undefined}
            onClick={() => setShowGxModal(true)}
          />

          {showGxModal && gxData && campaign && campaignId && (
            <GearExchangeModal
              data={gxData}
              campaign={campaign}
              campaignId={campaignId}
              formatLabel="Meta Flexible Ad"
              zoneLimits={[{ ratio: 'any', limit: 10, existing: cardsWithImages }]}
              onClose={() => setShowGxModal(false)}
              usedInVariants={usedInVariants}
              onImport={(files: GXImportedFile[]) => {
                addBulkFiles(files.map((f) => f.file));
              }}
            />
          )}
        </>
      }

      {/* ── Destination URL + CTA ────────────────────────────────────── */}
      <div className="grid grid-cols-[1fr_auto] gap-3 items-end">
        <div>
          <FieldLabel
            label="Destination URL"
            tooltip="Shared across all creatives. Make sure it matches the offer in your ads."
          />
          <input
            type="url"
            value={destinationUrl}
            onChange={(e) => handleUrlChange(e.target.value)}
            placeholder="https://www.ford.com/..."
            className={`${inputClass} ${showErrors && (urlIssue?.errors?.length ?? 0) > 0 ? 'border-red-400 focus:border-red-400' : ''}`}
          />
          <InlineIssue
            issue={urlIssue}
            showErrors={showErrors}
            showWarnings={showWarnings}
            feedback={feedback}
            feedbackPending={feedbackPending}
            onFeedback={onAIFeedback}
          />
        </div>
        <div className="w-36">
          <FieldLabel label="Call to Action" tooltip="One CTA shared across all creatives." />
          <select
            value={cta}
            onChange={(e) => handleCtaChange(e.target.value)}
            className={`${inputClass} cursor-pointer`}
          >
            {CTA_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Creatives ────────────────────────────────────────────────── */}
      <div onDragOver={onZoneDragOver} onDragLeave={onZoneDragLeave} onDrop={onZoneDrop}>
        <input
          ref={bulkInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) {
              addBulkFiles(e.target.files);
              e.target.value = '';
            }
          }}
        />

        <div className="flex items-center justify-between mb-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Creatives
            <span className="normal-case font-normal tracking-normal ml-1">
              ({cards.length} / {MAX_CARDS})
            </span>
          </p>
          <p className="text-[11px] text-slate-400">Click a creative to preview it</p>
        </div>
        <div className="border-b border-slate-200 mb-3" />

        <div
          className={`space-y-2 rounded-xl transition-all ${isDraggingFile ? 'ring-2 ring-[#0014cc]/30 bg-[#0014cc]/[0.02]' : ''}`}
        >
          {cards.map((card, i) => (
            <CardRow
              key={card.id}
              card={card}
              index={i}
              total={cards.length}
              isActive={i === activeCard}
              fieldIssues={fieldIssues}
              showErrors={showErrors}
              showWarnings={showWarnings}
              feedback={feedback}
              feedbackPending={feedbackPending}
              onFeedback={onAIFeedback}
              onSelect={() => selectCard(i)}
              onFileChange={(f) => handleFileChange(card.id, f)}
              onExistingImageClear={() => handleExistingImageClear(card.id)}
              onPrimaryTextChange={(v) => updateCard(card.id, { primaryText: v })}
              onHeadlineChange={(v) => updateCard(card.id, { headline: v })}
              onDescriptionChange={(v) => updateCard(card.id, { description: v })}
              onRemove={() => removeCard(card.id)}
            />
          ))}
        </div>

        {/* Aggregate (no-index) section-level messages — e.g. "5+ primary texts recommended" */}
        <InlineIssues
          issue={primaryTextIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
        <InlineIssues
          issue={headlineIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />
        <InlineIssues
          issue={descriptionIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />

        {/* Asset-level validation messages */}
        <InlineIssues
          issue={assetIssue}
          showErrors={showErrors}
          showWarnings={showWarnings}
          feedback={feedback}
          feedbackPending={feedbackPending}
          onFeedback={onAIFeedback}
        />

        {cards.length < MAX_CARDS && (
          <button
            type="button"
            onClick={() => bulkInputRef.current?.click()}
            className={`mt-2 w-full py-2.5 rounded-xl border-2 border-dashed hover:bg-slate-50 flex items-center justify-center gap-2 transition-all ${
              isDraggingFile ? 'border-[#0014cc]/50 bg-[#0014cc]/5 text-[#0014cc]' : addButtonBorder
            }`}
          >
            <Plus className="w-4 h-4" />
            <span className="text-xs font-medium">
              {isDraggingFile ? 'Drop files to add creatives' : 'Add creative or drop files here'}
            </span>
          </button>
        )}
      </div>

      {/* ── Legal ───────────────────────────────────────────────────── */}
      <Field
        label="Legal"
        tooltip="Required in some markets (e.g. France) for regulated categories. Internal only — not displayed in the mockup."
        fieldKey="legal"
        fieldIssues={fieldIssues}
        showErrors={showErrors}
        showWarnings={showWarnings}
        feedback={feedback}
        feedbackPending={feedbackPending}
        onFeedback={onAIFeedback}
        help={<>Enter "N/A" if your market does not require a legal disclaimer.</>}
      >
        <textarea
          value={legal}
          onChange={(e) => handleLegalChange(e.target.value)}
          placeholder="Legal disclaimer — required for some markets (e.g. FR)..."
          rows={5}
          className={`${inputClass} resize-none ${issueBorder(fieldIssues['legal'], showErrors, showWarnings)}`}
        />
      </Field>
    </div>
  );
}
